import { createContext, useContext, useMemo } from "react";
import { BreadTransaction } from "breadcommon";
import { getCurrencySymbol } from "../common/DollarAmount";
import { PreferencesContext } from "../firebaseio/PreferencesContext";

/**
 * The Amount column's width as CSS, sized to its longest amount, or undefined
 * for its default width. Provide it around both the column headers and the
 * table, so they agree.
 */
export const TransactionsAmountWidthContext = createContext<string | undefined>(
  undefined,
);

// Amounts are set in Roboto Mono, whose characters are all 0.6em wide, at the
// table's 0.9rem text size.
const AMOUNT_CHARACTER_WIDTH = "0.54rem";
// The narrowest the column can be: room for its header's label and its sort
// and filter icons.
const AMOUNT_COLUMN_MIN_WIDTH = "8.125rem";

/**
 * The Amount column's width for these transactions: enough for the longest
 * amount as the column shows it, with its currency symbol, cents and sign.
 */
export function useTransactionsAmountWidth(
  transactions:
    | readonly BreadTransaction[]
    | ReadonlyMap<string, BreadTransaction>,
): string {
  const userPrefs = useContext(PreferencesContext);
  const defaultCurrency = userPrefs?.web?.default_currency ?? "USD";
  return useMemo(() => {
    const format = new Intl.NumberFormat("en-US", {
      signDisplay: "never",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const symbols = new Map<string, string>();
    let longest = 0;
    const all = Array.isArray(transactions)
      ? transactions
      : Array.from(
          (transactions as ReadonlyMap<string, BreadTransaction>).values(),
        );
    for (const transaction of all) {
      const currency = transaction.currency_code ?? defaultCurrency;
      let symbol = symbols.get(currency);
      if (symbol === undefined) {
        symbol = getCurrencySymbol("en-US", currency);
        symbols.set(currency, symbol);
      }
      longest = Math.max(
        longest,
        symbol.length + format.format(transaction.amount).length,
      );
    }
    // One more character for the sign, then its 2px margin and the cell's
    // 0.75rem padding on each side.
    return `max(${AMOUNT_COLUMN_MIN_WIDTH}, calc(${longest + 1} * ${AMOUNT_CHARACTER_WIDTH} + 2px + 1.5rem))`;
  }, [transactions, defaultCurrency]);
}
